import { c as _c } from "react/compiler-runtime";
import { type ComponentPropsWithoutRef, type Dispatch, type SetStateAction, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;
interface AutoplayOptions {
  enabled: boolean;
  interval: number;
  next: () => number;
}
interface Autoplay {
  shouldAutoplay: boolean;
  autoplayUserPreference: boolean;
  setAutoplayUserPreference: Dispatch<SetStateAction<boolean>>;
  rootProps: ComponentPropsWithoutRef<'div'>;
}
export function useAutoplay(t0) {
  const $ = _c(17);
  const {
    enabled,
    interval,
    next
  } = t0;
  const [shouldAutoplay, setShouldAutoplay] = useState(enabled);
  const [autoplayUserPreference, setAutoplayUserPreference] = useState(enabled);
  let t1;
  if ($[0] !== next) {
    t1 = () => requestAnimationFrame(next);
    $[0] = next;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  useInterval(t1, autoplayUserPreference && shouldAutoplay ? interval : null);
  let t2;
  if ($[2] !== enabled) {
    t2 = () => {
      if (!enabled) {
        return;
      }
      setShouldAutoplay(false);
    };
    $[2] = enabled;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const pause = t2;
  let t3;
  if ($[4] !== enabled) {
    t3 = () => {
      if (!enabled) {
        return;
      }
      setShouldAutoplay(true);
    };
    $[4] = enabled;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const play = t3;
  let t4;
  let t5;
  if ($[6] !== pause || $[7] !== play) {
    t4 = () => {
      const listener = function listener() {
        if (document.visibilityState === "hidden") {
          pause();
        } else {
          play();
        }
      };
      document.addEventListener("visibilitychange", listener);
      return () => {
        document.removeEventListener("visibilitychange", listener);
      };
    };
    t5 = [pause, play];
    $[6] = pause;
    $[7] = play;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  useEffect(t4, t5);
  let t6;
  if ($[10] !== pause || $[11] !== play) {
    t6 = {
      onMouseEnter: pause,
      onTouchStart: pause,
      onFocus: pause,
      onMouseLeave: play,
      onTouchEnd: play,
      onBlur: play
    };
    $[10] = pause;
    $[11] = play;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== autoplayUserPreference || $[14] !== shouldAutoplay || $[15] !== t6) {
    t7 = {
      shouldAutoplay,
      autoplayUserPreference,
      setAutoplayUserPreference,
      rootProps: t6
    };
    $[13] = autoplayUserPreference;
    $[14] = shouldAutoplay;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}
export function useInterval(callback, delay) {
  const $ = _c(6);
  const savedCallback = useRef(callback);
  let t0;
  let t1;
  if ($[0] !== callback) {
    t0 = () => {
      savedCallback.current = callback;
    };
    t1 = [callback];
    $[0] = callback;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useIsomorphicLayoutEffect(t0, t1);
  let t2;
  let t3;
  if ($[3] !== delay) {
    t2 = () => {
      if (delay === null) {
        return;
      }
      const id = setInterval(() => {
        savedCallback.current();
      }, delay);
      return () => {
        clearInterval(id);
      };
    };
    t3 = [delay];
    $[3] = delay;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
}